/* eslint-disable eslint-comments/no-restricted-disable, max-lines -- File is too complex to refactor right now. Should be handled later */
import { useEffect, useMemo, useRef, useState } from "react";
import type { Editor } from "@tiptap/core";
import { EditorContent, useEditor } from "@tiptap/react";
import { debounce, isFunction, noop } from "lodash";
import { isMobile } from "react-device-detect";
import { useDropzone } from "react-dropzone";
import { useHotkeys } from "react-hotkeys-hook";
import { t } from "@/i18n-js/instance";
import { SendMessageConfirmationModal } from "@circle-react/components/Chat/sharedV2/SendMessageConfirmationModal";
import { SharePrivateMessageConfirmationModal } from "@circle-react/components/Chat/sharedV2/SharePrivateMessageConfirmationModal";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useBoolean } from "@circle-react/hooks/utils/useBoolean";
import { TextModifiersMenu } from "@circle-react-shared/uikit/TipTap/Extensions/TextModifiers";
import { useInternalEditorState } from "@circle-react-shared/uikit/TipTap/internalEditorState";
import { DragIndicator } from "./AttachmentPreviews/DragIndicator";
import { getTiptapExtensions } from "./Extensions";
import { MenuBar } from "./MenuBar";
import { PreviewBlocks } from "./PreviewBlocks";
import { TipTapEditorContext } from "./TipTapEditorContext";
import type { TiptapEditorProps } from "./TiptapEditorProps.types";
import { EMPTY_EDITOR_BLOCKS } from "./constants";
import { DEFAULT_CHAT_PROPS } from "./constants";
import { doesEditorContainsEditedLabel } from "./utilities";
import { clipboardTextParser } from "./utilities/clipboardTextParser";
import {
  editorHasNoMentionOrEntityOrMessage,
  sanitizeContent,
  sanitizeInputJson,
} from "./utilities/sanitizeContent";
import {
  generateLocalSgidToObjectMap,
  getSgidValuesFromHTML,
} from "./utilities/sgidForEditorContent";
import { useEditorLocalState } from "./utilities/useEditorLocalState";
import "./styles.scss";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    editedlabel: {
      addEditedLabelNode: () => ReturnType;
    };
  }
}

export const TiptapEditor = ({
  autofocus = false,
  chatProps = DEFAULT_CHAT_PROPS,
  chatRoomAccessArr = [],
  className,
  disabled = false,
  editable = true,
  editedAt,
  editorClassName,
  editorRef,
  isChatSpace = false,
  isLiveStream = false,
  isUpdate = false,
  maxLength,
  menuBarComponent: MenuBarComponent = MenuBar,
  menubarProps = {},
  messageBoxClassName,
  onBlur,
  onChange,
  onDelete,
  onFocus,
  onSubmit,
  placeholder = t("message_placeholder"),
  portalTargetId,
  rich_text_body = {},
  shouldDisableEntities = false,
  shouldDisableMentions = false,
  shouldRefetch,
  shouldScrollIntoView,
  shouldShowPreviewBlocks = true,
  shouldShowTextModifiersMenu = true,
  shouldSupportCodeBlocks = false,
  shouldSupportTables = false,
  showMenuBar = true,
  spaceId,
  submitOnEnter = true,
  type = "chat",
  value,
  isMediaUploadAllowed = true,
  ...passThroughProps
}: TiptapEditorProps) => {
  const [shouldShowConfirmationModal, toggleConfirmationModal] = useBoolean();
  const [
    shouldShowPrivateMessageConfirmationModal,
    togglePrivateMessageConfirmationModal,
  ] = useBoolean();
  const [
    shouldShowPrivateMessageConfirmationOnSubmit,
    setShouldShowPrivateMessageConfirmationOnSubmit,
  ] = useState(false);

  const isWorkflowType = type === "workflow";

  const richTextBody =
    editable && isWorkflowType
      ? {
          body: value?.body || value,
          sgids_to_object_map: value?.localSgidToObjectMap || {},
        }
      : rich_text_body;

  const {
    body: initialValue,
    attachments = [],
    sgids_to_object_map: sgidToObjectMap = {},
  } = richTextBody || {};

  const { arePopupsVisible } = useInternalEditorState();

  const {
    isFocused,
    setIsFocused,
    localSgidToObjectMap,
    addToLocalSgidToObjectMap,
    setLocalSgidToObjectMap,
    messageAttachments,
    addMessageAttachments,
    removeMessageAttachments,
    setMessageAttachments,
  } = useEditorLocalState({
    initialValue: isUpdate || isWorkflowType ? sgidToObjectMap : {},
  });

  const localSgidRef = useRef(localSgidToObjectMap);
  localSgidRef.current = localSgidToObjectMap;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localAttachments, setLocalAttachments] = useState<any>(
    editable ? attachments : [],
  );
  const [linkPreviews, setLinkPreviews] = useState<{
    [key: string]: boolean;
  }>(chatProps.unfurlUrls || {});

  const shouldShowEditedLabel = Boolean(editedAt);

  const debouncedOnChange = debounce(editor => {
    if (!isFunction(onChange)) return;
    const json = editor.getJSON();
    onChange({ body: json, localSgidToObjectMap: localSgidRef.current });
  }, 500);

  const sanitizedInitialValue = useMemo(() => {
    if (isWorkflowType) {
      return sanitizeInputJson(value?.body || value || initialValue);
    }

    return sanitizeInputJson(value || initialValue);
  }, [isWorkflowType, value, initialValue]);

  const editor = useEditor({
    editable,
    content: sanitizedInitialValue,
    onUpdate: ({ editor }) => {
      sanitizeContent({ editor });
      debouncedOnChange(editor);
    },
    onFocus: () => {
      setIsFocused(true);
      isFunction(onFocus) && onFocus();
    },
    onBlur: () => {
      setIsFocused(false);
      isFunction(onBlur) && onBlur();
    },
    autofocus,
    extensions: getTiptapExtensions({
      placeholder,
      maxLength,
      menubarProps,
      type,
      shouldDisableEntities,
      shouldDisableMentions,
      shouldSupportCodeBlocks,
      shouldSupportTables,
      portalTargetId,
    }),
    editorProps: {
      transformPastedHTML(html: any) {
        // Strip edited-label content from pasted HTML
        const sanitizedHTML = html.replace(
          /<edited-label[^>]*>.*?<\/edited-label>/gi,
          "",
        );

        const sgids: string[] = getSgidValuesFromHTML(sanitizedHTML);
        if (sgids.length) {
          void generateLocalSgidToObjectMap({
            sgids,
            setLocalSgidToObjectMap,
          });
        }
        return sanitizedHTML;
      },
      clipboardTextParser,
      attributes: {
        class: classNames(editorClassName, "tiptap-editor"),
      },
    },
  });

  useEffect(() => {
    if (editor?.isEditable) {
      if (isUpdate && chatProps.unfurlUrls) {
        setLinkPreviews(chatProps.unfurlUrls);
      } else {
        setLinkPreviews({});
      }
    } else {
      setLinkPreviews(chatProps.unfurlUrls || {});
    }
  }, [editor?.isEditable, isUpdate, chatProps.unfurlUrls]);

  const isUploading = localAttachments.some((a: any) => a.progress >= 0);
  const editorHasNoText = () =>
    editor?.state?.doc?.textContent.trim().length === 0;

  const editorHasNoContent = () =>
    editorHasNoMentionOrEntityOrMessage(editor) &&
    (editor?.isEmpty || editorHasNoText());

  const resetEditor = () => {
    editor?.commands.clearContent();
    setLocalAttachments([]);
    setLocalSgidToObjectMap({});
    setMessageAttachments([]);
    setLinkPreviews({});
  };

  const handleSubmit = async (skipConfirmationModal = false): Promise<void> => {
    const isEditorEmpty = editorHasNoContent() && !localAttachments.length;
    if ((isEditorEmpty && !isUpdate) || isUploading) return;
    if (isEditorEmpty && isUpdate) {
      return onDelete?.();
    }
    const mentions = Object.values(localSgidToObjectMap);
    const hasGroupMentions = mentions.some(
      (mention: any) => mention.type == "group_mention",
    );
    if (!skipConfirmationModal && hasGroupMentions) {
      toggleConfirmationModal();
      return;
    }
    if (shouldShowPrivateMessageConfirmationOnSubmit) {
      togglePrivateMessageConfirmationModal();
      return;
    }
    if (isFunction(onSubmit)) {
      const submissionPromise = onSubmit({
        body: editorHasNoContent() ? EMPTY_EDITOR_BLOCKS : editor?.getJSON(),
        attachments: localAttachments.map(({ signed_id }: any) => signed_id),
        localAttachments,
        localSgidToObjectMap,
        unfurlUrls: linkPreviews,
      });
      try {
        setIsSubmitting(true);
        await submissionPromise;
      } catch (e) {
        console.error(e);
      } finally {
        setIsSubmitting(false);
        resetEditor();
      }
    }
  };

  const isChineseOrJapaneseCharacter = (editor: Editor | null) => {
    if (!editor) return false;
    const text = editor.getText();

    const regex =
      /[\u3000-\u303F]|[\u3040-\u309F]|[\u30A0-\u30FF]|[\uFF00-\uFFEF]|[\u4E00-\u9FAF]|[\u2605-\u2606]|[\u2190-\u2195]|\u203B/; // Ref https://gist.github.com/ryanmcgrath/982242

    return regex.test(text);
  };

  useHotkeys(
    "enter",
    () => {
      void handleSubmit();
    },
    {
      enabled:
        isFocused &&
        submitOnEnter &&
        !arePopupsVisible &&
        !isMobile &&
        !isChineseOrJapaneseCharacter(editor),
      enableOnContentEditable: true,
    },
    [
      editor,
      localAttachments,
      arePopupsVisible,
      isFocused,
      shouldShowPrivateMessageConfirmationOnSubmit,
      linkPreviews,
    ],
  );

  const addFilesToAttachments = (files: any) =>
    setLocalAttachments((prevAttachments: any) => [
      ...prevAttachments,
      ...files.map((file: any) => ({
        file,
        url: URL.createObjectURL(file),
        filename: file.name,
        byte_size: file.size,
        content_type: file.type,
        toUpload: true,
        progress: 0,
      })),
    ]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openFilePicker,
  } = useDropzone({
    onDrop: isMediaUploadAllowed ? addFilesToAttachments : noop,
    noClick: true,
    disabled: !isMediaUploadAllowed,
  });

  const fileInputProps: any = getInputProps();

  useEffect(() => {
    if (shouldRefetch && !editable && editor) {
      editor.commands.setContent(sanitizedInitialValue); // Re rending editor after the initial value is changed on condition
    }
    if (
      !editable &&
      shouldShowEditedLabel &&
      editor &&
      "addEditedLabelNode" in editor.commands &&
      typeof editor.commands.addEditedLabelNode === "function"
    ) {
      requestAnimationFrame(() => {
        if (!doesEditorContainsEditedLabel(editor)) {
          editor.chain().focus("end").addEditedLabelNode().run();
        }
      });
    }
  }, [
    sanitizedInitialValue,
    editor,
    shouldRefetch,
    shouldShowEditedLabel,
    editable,
  ]);

  if (!editor) {
    return null;
  }

  if (editorRef) {
    editorRef.current = editor;
  }

  const contextValue: TipTapEditorContext = {
    addMessageAttachments,
    addToLocalSgidToObjectMap,
    attachments,
    chatProps,
    chatRoomAccessArr,
    editedAt,
    editor,
    isUpdate,
    localAttachments,
    localSgidToObjectMap,
    messageAttachments,
    portalTargetId,
    removeMessageAttachments,
    setLocalAttachments,
    setShouldShowPrivateMessageConfirmationOnSubmit,
    sgidToObjectMap,
    shouldRefetch,
    shouldScrollIntoView,
    spaceId,
    type,
    linkPreviews,
    setLinkPreviews,
    isWorkflowType,
  };

  const charactersCount = editor?.storage?.characterCount?.characters() || 0;
  const countText = `${String(charactersCount)}/${String(maxLength)}`;
  const newMessageBoxStyles =
    "bg-secondary-dark focus-within:bg-primary hover:bg-primary active:bg-primary hover:border-dark border-hover rounded-xl border active:border-very-dark focus-within:border-very-dark hover:focus-within:border-very-dark";

  const { isTyping } = chatProps;
  return (
    <TipTapEditorContext.Provider value={contextValue}>
      {!editor.isEditable ? (
        <>
          {!editorHasNoContent() && <EditorContent editor={editor} />}
          {shouldShowPreviewBlocks && !isUpdate && <PreviewBlocks />}
        </>
      ) : (
        <div
          className={classNames(
            "text-dark relative flex flex-col px-4 py-3 text-sm transition-colors",
            isChatSpace ? "md:mb-6" : "mb-6",
            newMessageBoxStyles,
            {
              "border-none": isDragActive,
              "border-light": !editorHasNoContent(),
              "pointer-events-none opacity-70": disabled || isSubmitting,
              "!mb-0": isTyping,
              "!bg-primary": isUpdate,
            },
            className,
          )}
          {...passThroughProps}
          {...getRootProps()}
          onPaste={e => {
            if (isMediaUploadAllowed && e.clipboardData?.files?.length > 0) {
              e.preventDefault();
              const files = Array.from(e.clipboardData.files);
              addFilesToAttachments(files);
            }
          }}
          data-testid="tiptap-editor"
        >
          {isDragActive && <DragIndicator />}
          <input {...fileInputProps} />
          <div
            className={classNames(
              "chat-message-word-wrap max-h-[300px] cursor-text overflow-auto",
              messageBoxClassName,
            )}
          >
            <EditorContent editor={editor} />
            {maxLength && <div className="text-light">{countText}</div>}
          </div>
          {editor && shouldShowTextModifiersMenu && (
            <TextModifiersMenu
              editor={editor}
              allowBubbleMenuOverflow={type !== "chat"}
            />
          )}
          {shouldShowPreviewBlocks && <PreviewBlocks />}
          {showMenuBar && (
            <MenuBarComponent
              isSubmitting={isSubmitting}
              isUploading={isUploading}
              openFilePicker={openFilePicker}
              editor={editor}
              handleSubmit={handleSubmit}
              editorHasNoContent={() =>
                isUpdate
                  ? false
                  : editorHasNoContent() && !localAttachments.length
              }
              isFocused={isFocused}
              isUpdate={isUpdate}
              isLiveStream={isLiveStream}
              portalTargetId={portalTargetId}
              {...menubarProps}
            />
          )}
        </div>
      )}
      <SendMessageConfirmationModal
        shouldShowConfirmationModal={shouldShowConfirmationModal}
        toggleConfirmationModal={toggleConfirmationModal}
        handleSubmit={handleSubmit}
        chatParticipantsCount={chatProps.chatParticipantsCount}
      />
      {shouldShowPrivateMessageConfirmationModal && (
        <SharePrivateMessageConfirmationModal
          shouldShowPrivateMessageConfirmationModal={
            shouldShowPrivateMessageConfirmationModal
          }
          togglePrivateMessageConfirmationModal={
            togglePrivateMessageConfirmationModal
          }
          handleSubmit={handleSubmit}
          setShouldShowPrivateMessageConfirmationOnSubmit={
            setShouldShowPrivateMessageConfirmationOnSubmit
          }
          shouldShowPrivateMessageConfirmationOnSubmit={
            shouldShowPrivateMessageConfirmationOnSubmit
          }
        />
      )}
    </TipTapEditorContext.Provider>
  );
};
